import { ActionIcon, Badge, Button, Flex, Group, Loader, Select } from "@mantine/core";
import {
    Add,
    AddOutlined,
    Close,
    DownloadOutlined,
    Send
} from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import {
    AutoHeightContainer,
    PageHeader,
    PrioritySearch,
    PriorityViewTable,
    removeTypename,
    useExportPriorityEntries,
    useGetPriorityViewsQuery,
    usePageSearch,
    useTherapproveDrawers,
    useTherapproveModals
} from "@therapprove/common";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const PRIORITY_PIPELINE_QUICK_FILTERS: Record<
    string,
    {
        label: string;
        searchBody: PrioritySearch;
    }
> = {
    "needs-follow-up": {
        label: "Needs Follow-up",
        searchBody: {
            where: {
                needs_follow_up: {
                    value: true,
                    amount: 7,
                    interval: "days"
                }
            }
        }
    },
    "without-assigned-provider": {
        label: "Without Assigned Provider",
        searchBody: {
            where: {
                without_assigned_provider: true
            }
        }
    }
};

export const PriorityEntries = () => {
    // Hooks
    const { search, setSearch, debouncedSearch } = usePageSearch();
    const { isExporting, exportPriorityEntries } = useExportPriorityEntries();
    const [searchParams, setSearchParams] = useSearchParams();
    const { priorityEntry } = useTherapproveDrawers();
    const { editPriorityView, priorityPublicInvite } = useTherapproveModals();

    // State
    const [openModal, setOpenModal] = useState<() => void>(() => { });
    const [activeViewId, setActiveViewId] = useState<string | null>(null);

    // Queries
    const [{ data, fetching }, refetchPriorityViews] = useGetPriorityViewsQuery({
        requestPolicy: "cache-and-network"
    });
    const priorityViews = data?.priorityViews?.priorityViews;
    const priorityStatuses = data?.priorityStatuses?.priorityStatuses ?? [];
    const activeQuickFilter =
        PRIORITY_PIPELINE_QUICK_FILTERS[searchParams.get("filter") ?? ""];

    const cleanHash = (str: string) =>
        str.toLowerCase().replace(/[^a-z0-9]+/g, "").trim();

    // Restore selected view from URL hash, or default to first view
    useEffect(() => {
        if (!priorityViews?.length) return;
        if (activeViewId) return;

        const hash = window.location.hash.replace("#", "");
        const match = hash
            ? priorityViews.find((v) => cleanHash(v?.name ?? "") === cleanHash(hash))
            : null;

        setActiveViewId(match?.priorityViewId ?? priorityViews[0]?.priorityViewId ?? null);
    }, [priorityViews]);

    // Persist selected view to URL hash
    const handleViewChange = (viewId: string | null) => {
        setActiveViewId(viewId);
        const view = priorityViews?.find((v) => v?.priorityViewId === viewId);
        if (view?.name) {
            window.location.hash = cleanHash(view.name);
        }
    };

    const activeView = priorityViews?.find(
        (v) => v?.priorityViewId === activeViewId
    );

    const viewSelectData = priorityViews?.map((v) => ({
        value: v?.priorityViewId ?? "",
        label: v?.name ?? ""
    })) ?? [];

    const mergeSearchBody = (searchBody?: PrioritySearch | null) => {
        const cleanSearchBody = removeTypename(searchBody) ?? undefined;
        if (!activeQuickFilter) {
            return cleanSearchBody;
        }

        return {
            ...cleanSearchBody,
            where: {
                ...cleanSearchBody?.where,
                ...activeQuickFilter.searchBody.where
            }
        };
    };

    return (
        <AutoHeightContainer style={{ gap: "0.5rem" }}>
            <PageHeader
                search={search}
                setSearch={setSearch}
                placeholder="Search referral pipeline..."
                leftSection={
                    <Select
                        data={viewSelectData}
                        value={activeViewId}
                        onChange={handleViewChange}
                        allowDeselect={false}
                        style={{ minWidth: 160 }}
                        rightSection={fetching && <Loader size={14} />}
                    />
                }
                rightSection={
                    <Flex gap="xs">
                        <Button
                            variant="subtle"
                            leftSection={<SvgIcon component={DownloadOutlined} />}
                            loading={isExporting}
                            disabled={Boolean(activeQuickFilter)}
                            onClick={() => exportPriorityEntries(activeView)}
                        >
                            Export
                        </Button>
                        <Button
                            variant="subtle"
                            leftSection={<SvgIcon component={AddOutlined} />}
                            onClick={() => {
                                editPriorityView.open({
                                    priorityStatusList: (priorityStatuses ??
                                        []) as Parameters<
                                            typeof editPriorityView.open
                                        >[0]["priorityStatusList"],
                                    refetchPriorityViews
                                });
                            }}
                        >
                            Add Custom View
                        </Button>
                        <Button
                            variant="subtle"
                            leftSection={<SvgIcon component={Add} />}
                            onClick={() => openModal?.()}
                        >
                            Add Client
                        </Button>
                        <Button
                            leftSection={<SvgIcon component={Send} />}
                            onClick={() => priorityPublicInvite.open()}
                        >
                            Invite Client
                        </Button>
                    </Flex>
                }
            />
            {activeQuickFilter && (
                <Group gap="xs">
                    <Badge
                        variant="light"
                        color="blue"
                        rightSection={
                            <ActionIcon
                                variant="transparent"
                                onClick={() => {
                                    const nextSearchParams =
                                        new URLSearchParams(searchParams);
                                    nextSearchParams.delete("filter");
                                    setSearchParams(nextSearchParams);
                                }}
                            >
                                <SvgIcon
                                    component={Close}
                                    style={{ fontSize: "1rem" }}
                                />
                            </ActionIcon>
                        }
                    >
                        Quick Filter: {activeQuickFilter.label}
                    </Badge>
                </Group>
            )}
            {activeView && (
                <PriorityViewTable
                    key={activeView.priorityViewId}
                    id={activeView.priorityViewId}
                    label={activeView.name}
                    search={debouncedSearch}
                    priorityStatusList={priorityStatuses}
                    options={{
                        searchBody: mergeSearchBody(activeView.filter),
                        setOpenModal: setOpenModal
                    }}
                    modalHandlers={{
                        priorityEntryDrawerOpen: () => priorityEntry.open()
                    }}
                />
            )}
        </AutoHeightContainer>
    );
};
